import {
  isPropertyValuePayload,
  type PropertyValuePayload,
} from '@couplespace/core/databases';

import type { LocalDatabase } from '../db/types';
import type { BlockRecord } from '../blocks/types';

import type { BlockPropertyRecord, DatabaseSchemaRecord } from './types';

type BlockRow = {
  id: string;
  parent_id: string | null;
  position: number;
  type: string;
  title: string | null;
  content: string;
  icon: string | null;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
  created_by: string | null;
  updated_by: string | null;
  actor_kind: string;
  vector_clock: string;
  sync_version: number;
};

type PropertyRow = {
  id: string;
  block_id: string;
  property_id: string;
  value: string;
  created_at: number;
  updated_at: number;
  updated_by: string | null;
  actor_kind: string;
  vector_clock: string;
  sync_version: number;
};

type SchemaRow = {
  id: string;
  database_id: string;
  columns: string;
  schema_version: number;
  updated_at: number;
  updated_by: string | null;
  actor_kind: string;
  vector_clock: string;
  sync_version: number;
};

type DatabaseRow = {
  id: string;
  block_id: string;
  created_at: number;
  updated_at: number;
  vector_clock: string;
  sync_version: number;
};

function rowToBlock(row: BlockRow): BlockRecord {
  return {
    id: row.id,
    parentId: row.parent_id,
    position: row.position,
    type: row.type,
    title: row.title,
    content: JSON.parse(row.content) as Record<string, unknown>,
    icon: row.icon,
    createdAt: new Date(row.created_at).toISOString(),
    updatedAt: new Date(row.updated_at).toISOString(),
    deletedAt: row.deleted_at ? new Date(row.deleted_at).toISOString() : null,
    createdBy: row.created_by,
    updatedBy: row.updated_by,
    actorKind: row.actor_kind,
    vectorClock: JSON.parse(row.vector_clock) as Record<string, number>,
    syncVersion: row.sync_version,
  };
}

function rowToProperty(row: PropertyRow): BlockPropertyRecord {
  return {
    id: row.id,
    blockId: row.block_id,
    propertyId: row.property_id,
    value: JSON.parse(row.value) as Record<string, unknown>,
    syncVersion: row.sync_version,
  };
}

export async function resolveDatabaseMeta(
  db: LocalDatabase,
  databaseBlockId: string,
): Promise<{ databaseId: string; databaseRowId: string } | null> {
  const row = await db.getFirstAsync<DatabaseRow>(
    `SELECT id, block_id FROM databases WHERE block_id = ?`,
    databaseBlockId,
  );
  if (!row) {
    return null;
  }
  return { databaseId: row.id, databaseRowId: row.id };
}

export async function loadSchemaLocal(
  db: LocalDatabase,
  databaseBlockId: string,
): Promise<DatabaseSchemaRecord | null> {
  const meta = await resolveDatabaseMeta(db, databaseBlockId);
  if (!meta) {
    return null;
  }
  const row = await db.getFirstAsync<SchemaRow>(
    `SELECT * FROM database_schemas WHERE database_id = ?`,
    meta.databaseId,
  );
  if (!row) {
    return null;
  }
  return {
    id: row.id,
    databaseId: row.database_id,
    columns: JSON.parse(row.columns) as DatabaseSchemaRecord['columns'],
    schemaVersion: row.schema_version,
    updatedAt: new Date(row.updated_at).toISOString(),
    syncVersion: row.sync_version,
  };
}

export async function listRowBlocksLocal(
  db: LocalDatabase,
  databaseBlockId: string,
): Promise<BlockRecord[]> {
  const rows = await db.getAllAsync<BlockRow>(
    `SELECT * FROM blocks WHERE parent_id = ? AND type = 'page' AND deleted_at IS NULL ORDER BY position ASC`,
    databaseBlockId,
  );
  return rows.map(rowToBlock);
}

export async function loadPropertiesForRowsLocal(
  db: LocalDatabase,
  rowIds: string[],
): Promise<Record<string, BlockPropertyRecord[]>> {
  if (rowIds.length === 0) {
    return {};
  }
  const placeholders = rowIds.map(() => '?').join(', ');
  const rows = await db.getAllAsync<PropertyRow>(
    `SELECT * FROM block_properties WHERE block_id IN (${placeholders})`,
    ...rowIds,
  );
  const out: Record<string, BlockPropertyRecord[]> = {};
  for (const row of rows) {
    const prop = rowToProperty(row);
    const list = out[prop.blockId] ?? [];
    list.push(prop);
    out[prop.blockId] = list;
  }
  return out;
}

export async function loadPropertyLocal(
  db: LocalDatabase,
  blockId: string,
  propertyId: string,
): Promise<BlockPropertyRecord | null> {
  const row = await db.getFirstAsync<PropertyRow>(
    `SELECT * FROM block_properties WHERE block_id = ? AND property_id = ?`,
    blockId,
    propertyId,
  );
  return row ? rowToProperty(row) : null;
}

export async function upsertPropertyRowLocal(
  db: LocalDatabase,
  prop: BlockPropertyRecord & {
    createdAt?: string;
    updatedAt?: string;
    updatedBy?: string | null;
    actorKind?: string;
    vectorClock?: Record<string, number>;
  },
): Promise<void> {
  const now = Date.now();
  await db.runAsync(
    `INSERT OR REPLACE INTO block_properties
      (id, block_id, property_id, value, created_at, updated_at, updated_by, actor_kind, vector_clock, sync_version)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    prop.id,
    prop.blockId,
    prop.propertyId,
    JSON.stringify(prop.value),
    prop.createdAt ? Date.parse(prop.createdAt) : now,
    prop.updatedAt ? Date.parse(prop.updatedAt) : now,
    prop.updatedBy ?? null,
    prop.actorKind ?? 'human',
    JSON.stringify(prop.vectorClock ?? {}),
    prop.syncVersion,
  );
}

export async function upsertSchemaRowLocal(
  db: LocalDatabase,
  schema: DatabaseSchemaRecord & {
    createdAt?: string;
    updatedBy?: string | null;
    actorKind?: string;
    vectorClock?: Record<string, number>;
  },
): Promise<void> {
  const updatedMs = Date.parse(schema.updatedAt);
  const createdMs = schema.createdAt ? Date.parse(schema.createdAt) : updatedMs;
  await db.runAsync(
    `INSERT OR REPLACE INTO database_schemas
      (id, database_id, columns, schema_version, created_at, updated_at, updated_by, actor_kind, vector_clock, sync_version)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    schema.id,
    schema.databaseId,
    JSON.stringify(schema.columns),
    schema.schemaVersion,
    createdMs,
    updatedMs,
    schema.updatedBy ?? null,
    schema.actorKind ?? 'human',
    JSON.stringify(schema.vectorClock ?? {}),
    schema.syncVersion,
  );
}

export async function upsertDatabaseRowLocal(
  db: LocalDatabase,
  row: {
    id: string;
    blockId: string;
    createdAt: string;
    updatedAt: string;
    syncVersion: number;
    vectorClock?: Record<string, number>;
  },
): Promise<void> {
  await db.runAsync(
    `INSERT OR REPLACE INTO databases (id, block_id, created_at, updated_at, vector_clock, sync_version)
     VALUES (?, ?, ?, ?, ?, ?)`,
    row.id,
    row.blockId,
    Date.parse(row.createdAt),
    Date.parse(row.updatedAt),
    JSON.stringify(row.vectorClock ?? {}),
    row.syncVersion,
  );
}

export async function loadTargetColumnsLocal(
  db: LocalDatabase,
  targetDatabaseBlockId: string,
): Promise<DatabaseSchemaRecord['columns']> {
  const schema = await loadSchemaLocal(db, targetDatabaseBlockId);
  return schema?.columns ?? [];
}

export async function loadTargetPropertyPayloadsLocal(
  db: LocalDatabase,
  relatedRowIds: string[],
  targetPropertyId: string,
): Promise<Map<string, PropertyValuePayload>> {
  const map = new Map<string, PropertyValuePayload>();
  if (relatedRowIds.length === 0) {
    return map;
  }
  const placeholders = relatedRowIds.map(() => '?').join(', ');
  const rows = await db.getAllAsync<PropertyRow>(
    `SELECT block_id, value FROM block_properties WHERE block_id IN (${placeholders}) AND property_id = ?`,
    ...relatedRowIds,
    targetPropertyId,
  );
  for (const row of rows) {
    const parsed = JSON.parse(row.value) as PropertyValuePayload;
    if (isPropertyValuePayload(parsed)) {
      map.set(row.block_id, parsed);
    }
  }
  return map;
}

export async function queryBlocksByIdsLocal(
  db: LocalDatabase,
  ids: string[],
): Promise<BlockRecord[]> {
  if (ids.length === 0) {
    return [];
  }
  const placeholders = ids.map(() => '?').join(', ');
  const rows = await db.getAllAsync<BlockRow>(
    `SELECT * FROM blocks WHERE id IN (${placeholders}) AND deleted_at IS NULL`,
    ...ids,
  );
  return rows.map(rowToBlock);
}
